<script setup lang="ts">
// One report, as moderators and admins see it: who reported whom and why, what was
// revealed (and whether franking verified it), its state, and what's been done.
import type { Report } from '../api/types'
import { displayName, formatTime } from '../lib/format'
import { describeAction, reasonLabel, STATUS_LABEL } from '../lib/reports'
import { useSession } from '../stores/session'

defineProps<{ report: Report; where?: string }>()
const session = useSession()
const name = (u: string | undefined) => (!u ? 'someone' : session.profiles.has(u) ? displayName(session.profiles, u) : u)
</script>

<template>
  <article class="border border-border p-3" :data-testid="`report-${report.status}`" :data-report="report.id">
    <header class="mono mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-text-muted">
      <span
        class="border px-1 uppercase"
        :class="report.reason === 'child_safety' ? 'border-signal text-signal' : 'border-border-strong text-text-secondary'"
      >{{ reasonLabel(report.reason) }}</span>
      <span class="uppercase" data-testid="report-status">{{ STATUS_LABEL[report.status] }}</span>
      <span v-if="where">{{ where }}</span>
      <span class="ml-auto">{{ formatTime(report.created_at) }}</span>
    </header>
    <p class="text-sm">
      <span class="text-text-secondary">{{ name(report.reporter) }}</span>
      <template v-if="report.library">
        <span class="text-text-muted"> reported a library file</span>
        <template v-if="report.reported">
          <span class="text-text-muted"> uploaded by </span>
          <span class="text-text-primary" data-testid="report-reported">{{ name(report.reported) }}</span>
        </template>
      </template>
      <template v-else>
        <span class="text-text-muted"> reported </span>
        <span class="text-text-primary" data-testid="report-reported">{{ name(report.reported) }}</span>
      </template>
    </p>
    <blockquote class="my-2 border-l border-border-strong pl-3 text-sm whitespace-pre-wrap text-text-secondary" data-testid="report-content">
      <template v-if="report.content.text">{{ report.content.text }}</template>
      <span v-if="report.content.attachment" class="mono block text-text-muted">{{ report.library ? 'Library' : 'File' }} · {{ report.content.attachment }}</span>
    </blockquote>
    <p
      class="mono"
      :class="report.verified ? 'text-text-secondary' : 'text-signal'"
      data-testid="report-verified"
      :title="
        report.library
          ? report.verified
            ? 'The key the reporter revealed opens the file stored in the library'
            : 'No file key revealed, or it didn’t open the stored file'
          : report.verified
            ? 'Franking matched: the server checked this against what the sender committed to when sending'
            : 'Couldn’t be checked: older than 30 days, from an older app, or not what was sent'
      "
    >
      {{
        report.library
          ? report.verified
            ? `✓ Verified: the file itself · added ${formatTime(report.content.sent_at)}`
            : 'Not verified: the file wasn’t included'
          : report.verified
            ? `✓ Verified: sent by ${name(report.reported)} · ${formatTime(report.content.sent_at)}`
            : 'Not verified'
      }}
    </p>
    <p v-if="report.note" class="mt-1 text-sm text-text-muted">“{{ report.note }}”</p>
    <p v-if="report.action" class="mono mt-2 text-text-secondary" data-testid="report-action">
      {{ describeAction(report.action, name) }} · {{ formatTime(report.action.at) }}
    </p>
    <p v-if="report.resolved_by" class="mono text-text-muted">Resolved by {{ name(report.resolved_by) }} · {{ formatTime(report.resolved_at ?? 0) }}</p>
    <div class="mt-2 flex flex-wrap gap-2 empty:hidden"><slot /></div>
  </article>
</template>
